import React, {RefObject} from "react";
import {connect} from "react-redux";
import {Alert, Typography} from "antd";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {
    AnyElementType,
    checkTranslation,
    ComparisonItem,
    CompositeType,
    EditorType,
    ElementsValues,
    ElementsValuesType,
    getComparisonElement,
    getElementsValues,
    Nullable,
    RefElementsMap,
    SchemaElementTypes,
    SegmentType,
    SignTokenType,
    SpanType,
} from "@data-force/app/";
import "./Instructions.scss";
import {ElementsValuesMap} from "@data-force/app/interfaces/store/reducers/schema";
import StoreState from "../../interfaces/store/storeState";
import HistoryParams from "../../interfaces/historyParams";
import {getHistoryParams} from "../../utils/common";
import MarkdownMedia from "../Markdown/MarkdownMedia";

interface InstructionsConfig {
    label: Nullable<string>
    description: string
    key: string // to be used as part of instruction element key
    displayLabel: boolean
}

interface ReferenceData {
    root: AnyElementType
    references: string[]
}

interface DescriptionOverrideData {
    editorRefElements: RefElementsMap
    overrideReference: string
    description: Nullable<string>
}

interface StateProps {
    editor: Nullable<EditorType>
    schemaId: string
    translate: TranslateFunction
    jobId: Nullable<string>
    schemaKey: string
    elementsValuesMap: ElementsValuesMap
}

interface OwnProps {
    headerReference: RefObject<HTMLHeadingElement>
}

export type Props = StateProps & OwnProps;

/**
 *  Renders 'Instructions' part of the view
 */
class Instructions extends React.Component<Props> {
    private static translationPath: string = "components.Instructions.Instructions";

    /**
     * Creates displayable instruction objects.
     * @param  {string} instructions - array of instructions
     * @param {string} key - to be used as part of instruction element key
     * @return {React.ReactNode} instructions node
     */
    static generateInstructions(instructions: string, key: string): React.ReactNode {
        return <MarkdownMedia signType={SignTokenType.BATCH} key={key}>{instructions}</MarkdownMedia>;
    }

    /**
     * Creates displayable label objects.
     *
     * @param {Nullable<string>} label - label for instructions
     * @param {string} key - to be used as part of instruction element key
     *
     * @return {React.ReactNode}
     */
    static generateLabel(label: Nullable<string>, key: string): React.ReactNode {
        const labelNode: React.ReactNode = label
            ? [label]
            : <Translate id={`${Instructions.translationPath}.generalInstructions.text`}/>;
        return (
            <Typography.Title level={5} key={`label-${key}`}>
                <p>{labelNode}</p>
            </Typography.Title>
        );
    }

    constructor(props: Props) {
        super(props);

        this.getElementFieldReferences = this.getElementFieldReferences.bind(this);
        this.createInstructions = this.createInstructions.bind(this);
        this.collectMetadataReferences = this.collectMetadataReferences.bind(this);
        this.parseReferences = this.parseReferences.bind(this);
        this.createEditorInstructions = this.createEditorInstructions.bind(this);
        this.parseEditorType = this.parseEditorType.bind(this);
        this.getLocalizedText = this.getLocalizedText.bind(this);
    }

    /**
     * For provided element, creates array of references that can contain instructions.
     *
     * @param {AnyElementType} element
     * @return {string[]}
     */
    getElementFieldReferences(element: AnyElementType): string[] {
        let elementFieldReferences: string[] = [];
        switch (element.schemaType) {
            case SchemaElementTypes.SPAN_TYPE: {
                const {fieldReferences, tagReference}: SpanType = element as SpanType;
                if (fieldReferences) {
                    elementFieldReferences = fieldReferences;
                } else if (tagReference) {
                    elementFieldReferences = [tagReference];
                }
                break;
            }
            case SchemaElementTypes.SEGMENT_TYPE: {
                const {spanReferences}: SegmentType = element as SegmentType;
                elementFieldReferences = spanReferences;
                break;
            }
            case SchemaElementTypes.COMPOSITE_FIELD_TYPE:
            case SchemaElementTypes.DECORATION_TYPE: {
                const {fieldReferences}: CompositeType = element as CompositeType;
                elementFieldReferences = fieldReferences;
                break;
            }

            default:
                break;
        }

        return elementFieldReferences;
    }

    /**
     *  Returns translated text, if translation is available.
     *  Otherwise, it returns original text.
     *
     * @param {string} text
     * @return {string}
     */
    getLocalizedText(text: string): string {
        const {translate}: StateProps = this.props;
        const textLocalizationId: string = `_configurableManifoldDescriptions.${text}.text`;
        return checkTranslation(textLocalizationId, text, translate);
    }

    /**
     * Creates displayable instruction node.
     *
     * @param {InstructionsConfig} instructionsConfig
     * @return {React.ReactNode[]}
     */
    createInstructions(instructionsConfig: InstructionsConfig): React.ReactNode[] {
        const {
            label, description, key, displayLabel,
        }: InstructionsConfig = instructionsConfig;
        const allInstructions: React.ReactNode[] = [];
        const localizedDescription: string = this.getLocalizedText(description);

        if (localizedDescription) {
            if (displayLabel && label) {
                allInstructions.push(Instructions.generateLabel(label, key));
            }
            allInstructions.push(Instructions.generateInstructions(localizedDescription, key));
        }

        return allInstructions;
    }

    /**
     * Creates array of metadata references, from editor and its span references.
     *
     * @param {EditorType} collection
     * @param {string[]} spanReferences - span element names
     * @return {string[]} - metadata references
     */
    collectMetadataReferences(collection: EditorType, spanReferences: string[]): string[] {
        const collectionMetadataReferences: string[] = collection.metaDataReferences
            ? collection.metaDataReferences
            : [];
        let spanMetadataReferences: string[] = [];

        if (collection.refElements) {
            spanMetadataReferences = spanReferences.reduce((prev, cur) => {
                const span = collection.refElements![cur] as SpanType;
                const {metaDataReferences}: SpanType = span;
                let accumulated: string[] = prev.slice();
                if (metaDataReferences) {
                    accumulated = accumulated.concat(metaDataReferences);
                }
                return accumulated as string[];
            }, [] as string[]);
        }

        return collectionMetadataReferences.concat(spanMetadataReferences);
    }

    /**
     * Iterates through provided references.
     * If reference is a member of "root.refElements", has "description" and satisfies the "batchWrite" condition,
     * parse it as instruction.
     *
     * @param {ReferenceData} referenceData
     * @return {React.ReactNode[]} allInstructions - displayable instruction nodes
     */
    parseReferences(referenceData: ReferenceData): React.ReactNode[] {
        const {root, references}: ReferenceData = referenceData;
        let instructions: React.ReactNode[] = [];
        if (root.refElements) {
            references.forEach((reference: string, index: number) => {
                const element: AnyElementType = root.refElements![reference];
                const {label, description}: AnyElementType = element;
                const elementFieldReferences: string[] = this.getElementFieldReferences(element);

                if (description) {
                    const itemKey = `${reference}-${index}`;
                    instructions.push(this.createInstructions({
                        label,
                        description,
                        key: itemKey,
                        displayLabel: true,
                    }));
                }

                if (elementFieldReferences.length > 0 && element.refElements) {
                    instructions = instructions.concat(
                        this.parseReferences({
                            root: element,
                            references: elementFieldReferences,
                        }),
                    );
                }
            });
        }

        return instructions;
    }

    /**
     * Resolve value of batchDescriptionOverrideReference.
     *
     * @param {DescriptionOverrideData} data
     * @return {Nullable<string>}
     */
    resolveOverrideReference(data: DescriptionOverrideData): Nullable<string> {
        const {editorRefElements, overrideReference, description}: DescriptionOverrideData = data;
        const {jobId, schemaKey, elementsValuesMap}: Props = this.props;
        const descriptionElement: Nullable<AnyElementType> = editorRefElements[overrideReference];

        let referenceValue: Nullable<string> = description;
        if (descriptionElement && jobId) {
            const elementsValues: ElementsValues = getElementsValues({
                metadataId: jobId,
                elementsValuesType: ElementsValuesType.JOB,
                schemaKey,
                elementsValuesMap,
            });

            const {value}: ComparisonItem = getComparisonElement(descriptionElement, elementsValues);
            if (value) {
                referenceValue = value as string;
            }
        }
        return referenceValue;
    }

    /**
     * If override is present, use override description.
     * Otherwise, use default schema description.
     *
     * @param {EditorType} editor
     * @return {Nullable<string>}
     */
    resolveEditorDescription(editor: EditorType): Nullable<string> {
        const {description, batchDescriptionOverrideReference, refElements}: EditorType = editor;
        return batchDescriptionOverrideReference
            ? this.resolveOverrideReference({
                editorRefElements: refElements || {},
                overrideReference: batchDescriptionOverrideReference,
                description,
            })
            : description;
    }

    /**
     * Creates editor element instructions.
     *
     * @param collectionElement
     * @return React.ReactNode[] - displayable instruction nodes
     */
    createEditorInstructions(collectionElement: EditorType): React.ReactNode[] {
        const {label}: EditorType = collectionElement;
        let instructions: React.ReactNode[] = [];
        const description: Nullable<string> = this.resolveEditorDescription(collectionElement);
        if (description) {
            instructions = this.createInstructions({
                label,
                description,
                key: "collection-instructions",
                displayLabel: false,
            });
        }
        return instructions;
    }

    /**
     * Adds instructions of provided referenceData to allInstructions collection.
     *
     * @param {React.ReactNode[]} allInstructions
     * @param {ReferenceData} referenceData
     * @param {string} key
     * @return {React.ReactNode[]}
     */
    addReferenceInstructions(
        allInstructions: React.ReactNode[],
        referenceData: ReferenceData,
        key: string,
    ): React.ReactNode[] {
        let instructions = allInstructions.slice();
        const referenceInstructions: React.ReactNode[] = this.parseReferences(referenceData);
        instructions = instructions.concat(referenceInstructions);
        if (referenceInstructions.length > 0) {
            instructions.push(<hr key={`${key}-break`}/>);
        }
        return instructions;
    }

    /**
     *  Gathers instructions from editor element, and all its references.
     *
     * @return React.ReactNode[] - displayable instruction nodes
     */
    parseEditorType(): React.ReactNode[] {
        let instructions: React.ReactNode[] = [];
        const {editor}: Props = this.props;
        if (editor) {
            // @ts-ignore
            const batchReferences: string[] = editor.batchFieldReferences
                // @ts-ignore
                ? editor.batchFieldReferences
                : [];
            const segmentReferences: string[] = editor.segmentReference
                ? [editor.segmentReference]
                : [];
            const spanReferences: string[] = editor.spanReferences
                ? editor.spanReferences
                : [];
            const metadataReferences: string[] = this.collectMetadataReferences(editor, spanReferences);

            const batchReferenceData: ReferenceData = {
                root: editor as AnyElementType,
                references: batchReferences,
            };
            const segmentReferenceData: ReferenceData = {
                root: editor as AnyElementType,
                references: segmentReferences,
            };
            const metaReferenceData: ReferenceData = {
                root: editor as AnyElementType,
                references: metadataReferences,
            };

            const editorInstructions: React.ReactNode[] = this.createEditorInstructions(editor);
            instructions = instructions.concat(editorInstructions);
            if (editorInstructions.length > 0) {
                instructions.push(<hr key="ci-break"/>);
            }
            instructions = this.addReferenceInstructions(instructions, batchReferenceData, "batch");
            instructions = this.addReferenceInstructions(instructions, segmentReferenceData, "segment");
            instructions = this.addReferenceInstructions(instructions, metaReferenceData, "metadata");
        }

        return instructions;
    }

    render(): React.ReactNode {
        const {translate, headerReference}: Props = this.props;
        return (
            <div className="df-instructions">
                <h2
                    className="df-instructions-title"
                    ref={headerReference}
                    tabIndex={-1}
                >
                    <Translate id={`${Instructions.translationPath}.headerTitle.text`}/>
                </h2>
                <Alert
                    message={translate(`${Instructions.translationPath}.disclaimerTitle.text`)}
                    description={translate(`${Instructions.translationPath}.disclaimerText.text`)}
                    type="info"
                    showIcon
                />
                <div className="df-instructions-container">
                    {this.parseEditorType()}
                </div>
            </div>
        );
    }
}

function mapStateToProps({
    localize,
    schema: {parsedSchemas, elementsValuesMap},
    project,
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;

    return {
        editor: parsedSchemas[schemaKey] as EditorType,
        schemaId: project?.projectDetails?.schemaId || "",
        translate: getTranslate(localize),
        jobId: historyParams?.jobId,
        schemaKey,
        elementsValuesMap,
    };
}

export default connect(mapStateToProps)(Instructions);
